import React from 'react';
import { ButtonProps } from './button.interfaces';
import { twMerge } from 'tailwind-merge';
import IconComponent from 'components/iconComponent';
import Spinner from 'components/Spinner';

const ButtonComponent: React.FC<ButtonProps> = ({
  borderRadius,
  children,
  desktopFullWidth = false,
  onClick,
  disabled = false,
  paddingX,
  paddingY,
  type,
  Icon,
  fill,
  fontSize,
  fontWeightUnderline,
  stroke,
  hoverFill,
  hoverStroke,
  iconPosition = 'right',
  iconWidth,
  iconHeight,
  testID,
  className = '',
  containerClassName = '',
  noUpperCase = false,
  fullWidthClassName = 'w-full',
  showSpinner = false,
  dataTooltipId,
  htmlButtonType = 'submit',
}) => {
  const commonClasses = `${fullWidthClassName} desktop:${
    desktopFullWidth ? fullWidthClassName : 'w-auto'
  } ${borderRadius ? borderRadius : 'rounded-5'} ${
    fontSize ? fontSize : 'desktop:text-sm text-xs'
  }
  text-center text-base font-bold cursor-pointer`;
  const disabledClasses =
    'disabled:opacity-35 disabled:cursor-not-allowed disabled:transition-none';
  const containerOrientationClass =
    iconPosition === 'top' ? 'flex-col' : 'flex-row';
  let baseClasses;
  let hoverClasses;
  let activeClasses;
  let paddingXClass;
  let paddingYClass;

  switch (type) {
    case 'outlined':
      baseClasses = `text-clc-blue bg-white border-solid border-2 border-clc-blue ${
        noUpperCase ? '' : 'uppercase'
      }`;
      hoverClasses = 'hover:bg-clc-blue hover:text-white';
      activeClasses =
        'enabled:active:bg-clc-blue-medium enabled:active:text-white';
      paddingXClass = paddingX ? paddingX : 'px-[18px]';
      paddingYClass = paddingY ? paddingY : 'py-[9.5px]';
      break;
    case 'outlined-transparent':
      baseClasses = `text-clc-blue bg-transparent border-solid border-2 border-clc-blue ${
        noUpperCase ? '' : 'uppercase'
      }`;
      hoverClasses = 'enabled:hover:bg-clc-blue enabled:hover:text-white';
      activeClasses =
        'enabled:active:bg-clc-blue-medium enabled:active:text-white';
      paddingXClass = paddingX ? paddingX : 'px-[18px]';
      paddingYClass = paddingY ? paddingY : 'py-[9.5px]';
      break;
    case 'icon':
      baseClasses = 'desktop:max-w-[45px] h-[45px] text-clc-blue bg-white';
      hoverClasses = 'enabled:hover:text-white enabled:hover:bg-clc-blue';
      activeClasses =
        'enabled:active:bg-clc-blue-medium enabled:active:text-white';
      paddingXClass = paddingX ? paddingX : 'px-[18px]';
      paddingYClass = paddingY ? paddingY : 'py-[9.5px]';
      break;
    case 'underline':
      baseClasses = `text-h7 ${
        fontWeightUnderline || 'font-semibold'
      } text-clc-blue underline ${noUpperCase ? '' : 'uppercase'}`;
      paddingXClass = paddingX ? paddingX : 'px-0';
      paddingYClass = paddingY ? paddingY : 'py-0';
      break;
    case 'social':
      baseClasses =
        'text-dark-gray bg-white border-solid border border-black-blur';
      paddingXClass = paddingX ? paddingX : 'px-10';
      paddingYClass = paddingY ? paddingY : 'py-[10.5px]';
      break;
    default:
      baseClasses = `text-white bg-clc-blue border-solid border-2 border-clc-blue ${
        noUpperCase ? '' : 'uppercase'
      }`;
      hoverClasses = 'enabled:hover:text-clc-blue enabled:hover:bg-white';
      activeClasses =
        'enabled:active:bg-clc-blue-medium enabled:active:text-white';
      paddingXClass = paddingX ? paddingX : 'px-[18px]';
      paddingYClass = paddingY ? paddingY : 'py-2.5';
  }

  return (
    <button
      data-tooltip-id={dataTooltipId?.toString()}
      data-testid={testID}
      className={twMerge(
        commonClasses,
        baseClasses,
        hoverClasses,
        activeClasses,
        paddingXClass,
        paddingYClass,
        disabledClasses,
        'group duration-300 transition-all',
        className,
      )}
      onClick={onClick}
      disabled={disabled}
      type={htmlButtonType}
    >
      {disabled && showSpinner ? (
        <Spinner className="mx-auto" />
      ) : (
        <div
          className={`flex text-sm items-center justify-center gap-2.5 ${containerClassName} ${containerOrientationClass}`}
        >
          {(iconPosition === 'top' || iconPosition === 'left') && Icon && (
            <IconComponent
              width={iconWidth}
              height={`${iconHeight} max-h-[28px]`}
              icon={Icon}
              fill={fill || 'fill-current'}
              stroke={stroke || 'stroke-current'}
              hoverStroke={hoverStroke}
              hoverFill={hoverFill}
            />
          )}
          {children}
          {iconPosition === 'right' && Icon && (
            <>
              <IconComponent
                width={iconWidth}
                height={`${iconHeight} max-h-[28px]`}
                icon={Icon}
                fill={fill || 'fill-current'}
                stroke={stroke || 'stroke-current'}
                hoverStroke={hoverStroke}
                hoverFill={hoverFill}
              />
            </>
          )}
        </div>
      )}
    </button>
  );
};

export default ButtonComponent;
